import React from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Popconfirm, Popover, Tooltip } from 'antd';
import PropTypes from 'prop-types';

import { useAllowedRoleChecker } from 'packages/utils';

import ComponentBlocker from '../ComponentBlocker';
import IntlMessages from 'util/IntlMessages';

import styles from './styles.module.less';

const ActionButton = ({
  disabledScaling = false,
  className = '',
  label = '',
  icon = null,
  allowedRoles = null,
  divId = null,
  onClick = () => {},
  disabled = false,
  type = 'text',
  tooltipText = null,
  tooltipPosition = 'topRight',
  color = 'primary',
  size = 'medium',
  confirm = null,
  ...props
}) => {
  const colorVariants = {
    primary: styles.primary,
    secondary: styles.secondary,
    danger: styles.danger,
    dark: styles.dark,
    soft: styles.soft,
    default: styles.default,
    light: styles.light,
    purple: styles.purple,
  };

  const colorVariant = `${colorVariants[color] || styles.primary}`;
  const isDestructiveConfirm = Boolean(confirm) && color === 'danger';
  const confirmTitle =
    confirm && typeof confirm === 'object' && confirm.title != null ? confirm.title : confirm;
  const dangerLabelClass = label && color === 'danger' ? styles.dangerLabel : '';
  const labeledClass = label ? styles.btnWithText : '';

  const buttonNode =
    !label && icon ? (
      <Button
        {...props}
        className={`${styles.actionButton} gx-mr-0 gx-mb-0 ${className} gx-rounded-circle ${disabledScaling ? 'disabledScaling' : ''}`}
        type={type}
        icon={<span className={`material-icons-outlined ${colorVariant}`}>{icon}</span>}
        onClick={confirm ? undefined : onClick}
        size={size}
        shape={'circle'}
        disabled={disabled}
      />
    ) : (
      <Button
        {...props}
        className={`${styles.actionButton} ${labeledClass} gx-mr-0 gx-mb-0 ${className} ${dangerLabelClass} ${disabledScaling ? 'disabledScaling' : ''}`}
        type={type}
        icon={
          icon ? <span className={`material-icons-outlined ${colorVariant}`}>{icon}</span> : null
        }
        onClick={confirm ? undefined : onClick}
        size={size}
        disabled={disabled}
      >
        {label}
      </Button>
    );

  return (
    <ComponentBlocker allowedRoles={allowedRoles} divId={divId}>
      {tooltipText ? (
        <Tooltip placement={tooltipPosition} title={tooltipText}>
          {confirmTitle ? (
            <Popconfirm
              title={confirmTitle}
              onConfirm={onClick}
              icon={<ExclamationCircleOutlined className={styles.popconfirmWarningIcon} />}
              okText={<IntlMessages id={isDestructiveConfirm ? 'button.delete' : 'button.yes'} />}
              cancelText={
                <IntlMessages id={isDestructiveConfirm ? 'button.cancel' : 'button.no'} />
              }
              okButtonProps={isDestructiveConfirm ? { danger: true } : undefined}
              overlayClassName={styles.actionButtonPopconfirm}
              placement="topRight"
            >
              {buttonNode}
            </Popconfirm>
          ) : (
            buttonNode
          )}
        </Tooltip>
      ) : confirmTitle ? (
        <Popconfirm
          title={confirmTitle}
          onConfirm={onClick}
          icon={<ExclamationCircleOutlined className={styles.popconfirmWarningIcon} />}
          okText={<IntlMessages id={isDestructiveConfirm ? 'button.delete' : 'button.yes'} />}
          cancelText={<IntlMessages id={isDestructiveConfirm ? 'button.cancel' : 'button.no'} />}
          okButtonProps={isDestructiveConfirm ? { danger: true } : undefined}
          overlayClassName={styles.actionButtonPopconfirm}
          placement="topRight"
        >
          {buttonNode}
        </Popconfirm>
      ) : (
        buttonNode
      )}
    </ComponentBlocker>
  );
};

ActionButton.propTypes = {
  icon: PropTypes.string,
  label: PropTypes.string,
  allowedRoles: PropTypes.array,
  divId: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  tooltipText: PropTypes.string,
  tooltipPosition: PropTypes.string,
  color: PropTypes.string,
  size: PropTypes.string,
  confirm: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.shape({ title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]) }),
  ]),
};

const ActionButtonContainer = ({
  buttons,
  maxVisibleButtons = 2,
  overflowVariant = 'icons',
  overflowPlacement = 'topRight',
  overflowTrigger = null,
}) => {
  const [overflowOpen, setOverflowOpen] = React.useState(false);
  const canAccess = useAllowedRoleChecker();
  // Blocked buttons render null, so counting the raw array would leave an empty overflow menu.
  const allowedButtons = buttons.filter(
    button => button && canAccess(button.props?.allowedRoles, button.props?.divId),
  );
  const hasMoreButtons = allowedButtons.length > maxVisibleButtons;
  const visibleButtons = hasMoreButtons
    ? allowedButtons.slice(0, maxVisibleButtons)
    : allowedButtons;
  const hiddenButtons = hasMoreButtons ? allowedButtons.slice(maxVisibleButtons) : [];
  const showLabeledMenu = overflowVariant === 'labeled';

  const closeOverflow = () => setOverflowOpen(false);

  const cloneOverflowButton = (button, index) => {
    const key = button.key ?? index;
    const originalOnClick = button.props.onClick;
    const onClick = (...args) => {
      closeOverflow();
      originalOnClick?.(...args);
    };

    if (showLabeledMenu) {
      return React.cloneElement(button, {
        key,
        label: button.props.label || button.props.tooltipText,
        tooltipText: null,
        disabledScaling: true,
        block: true,
        className: `${button.props.className || ''} ${styles.actionButtonMenuItem}`.trim(),
        onClick,
      });
    }

    return React.cloneElement(button, { key, onClick });
  };

  const popoverContent = (
    <div
      className={
        showLabeledMenu
          ? styles.actionButtonMenuItems
          : 'gx-d-flex gx-justify-content-center gx-flex-row gx-gap-2'
      }
    >
      {hiddenButtons.map(cloneOverflowButton)}
    </div>
  );

  const defaultOverflowTrigger = (
    <Button
      className={`${styles.actionButton} gx-mr-0 gx-mb-0`}
      type="text"
      icon={<span className={`material-icons-outlined ${styles.primary}`}>more_vert</span>}
      size="medium"
      shape="circle"
    />
  );

  return (
    <div className={`gx-d-flex gx-gap-2 ${styles.actionButtonsContainer}`}>
      {visibleButtons.map((button, index) => {
        return React.cloneElement(button, { key: button.key ?? index });
      })}

      {hasMoreButtons && (
        <Popover
          content={popoverContent}
          trigger="click"
          placement={overflowPlacement}
          visible={overflowOpen}
          onVisibleChange={setOverflowOpen}
          overlayClassName={
            showLabeledMenu ? styles.actionButtonPopoverLabeled : styles.actionButtonPopover
          }
        >
          {overflowTrigger ?? defaultOverflowTrigger}
        </Popover>
      )}
    </div>
  );
};

ActionButtonContainer.propTypes = {
  buttons: PropTypes.arrayOf(PropTypes.node).isRequired,
  maxVisibleButtons: PropTypes.number,
  overflowVariant: PropTypes.oneOf(['icons', 'labeled']),
  overflowPlacement: PropTypes.string,
  overflowTrigger: PropTypes.node,
};

ActionButton.Container = ActionButtonContainer;

export default ActionButton;
